import { z } from "zod";

/**
 * Zod schemas for each app setting's value.
 * These define the expected structure and validation rules for setting values.
 */
export const appSettingSchemas = {
  /**
   * Schema for configurable email sender identities per email type.
   * Keys are email type identifiers (e.g., "welcome", "generation_complete").
   * Values define the display name and address used in the From header.
   *
   * Key space is (product, emailType): wonda keeps the bare emailType keys
   * (all existing rows stay valid, no migration), other products use
   * "<product>:<emailType>" entries in the same flat record (mirrors
   * getCliVersionSettingKey). Lookup lives in
   * packages/features/src/notifications/resolve-email-sender.ts.
   */
  emailSenders: z.record(
    z.string(),
    z.object({ name: z.string(), address: z.string().email() }),
  ),
  /**
   * Schema for CLI version info returned by the public /cli/version endpoint.
   * Controls update notifications and forced updates for the wonda CLI.
   * `updateCommands` carries per-install-channel update instructions (pkg is a
   * download URL, mcpb is a human instruction); `updateCommand` is the legacy
   * single curl command kept for deployed clients.
   */
  cliVersion: z.object({
    latest: z.string(),
    minSupported: z.string(),
    updateCommand: z.string(),
    updateCommands: z
      .object({
        curl: z.string(),
        brew: z.string(),
        npm: z.string(),
        pkg: z.string(),
        mcpb: z.string(),
        // Windows installer download URL. Optional so rows stored before the
        // Windows installer shipped still parse.
        windows: z.string().optional(),
      })
      .optional(),
  }),
  /**
   * Arms the HTTP 426 upgrade_required backstop for Wonda-CLI requests below
   * cliVersion.minSupported. The gate only fires when `enforceAfter` is set
   * AND in the past; absent row / null / future date = never block. Accepts
   * any string Date.parse understands (date-only "2026-07-10" included) so an
   * ops hand-edit doesn't silently fail parsing and leave the gate unarmed.
   * Keep it unarmed until relay self-update has shipped and propagated (at
   * least two releases), or curl-piped CI users on old binaries get stranded.
   */
  cliVersionEnforceAfter: z.object({
    enforceAfter: z
      .string()
      .refine((value) => !Number.isNaN(Date.parse(value)), {
        message: "must be a date string Date.parse understands",
      })
      .nullable(),
  }),
  /**
   * Schema for Apify capacity budget (total slots available for concurrent actors).
   * Higher values allow more concurrent Apify actor instances but increase resource usage.
   */
  apifyCapacityBudget: z.object({
    slots: z.number().int().positive().finite(),
  }),
  /**
   * Schema for Apify lease duration (how long a capacity slot reservation is held).
   * Should be slightly longer than the maximum expected actor runtime.
   */
  apifyLeaseDurationMs: z.object({
    milliseconds: z.number().int().positive().finite(),
  }),
} as const satisfies Record<string, z.ZodTypeAny>;

/**
 * Storage keys for each app setting (used in the database).
 * The `satisfies` constraint ensures these keys stay in sync with the schemas.
 */
export const AppSetting = {
  emailSenders: "email.senders",
  cliVersion: "cli.version",
  cliVersionEnforceAfter: "cli.version_enforce_after",
  apifyCapacityBudget: "apify.capacity_budget",
  apifyLeaseDurationMs: "apify.lease_duration_ms",
} as const satisfies Record<keyof typeof appSettingSchemas, string>;

/** Name of an app setting (key in the appSettingSchemas object) */
export type AppSettingName = keyof typeof appSettingSchemas;

/**
 * Per-product storage key for the CLI version feed. All products share the
 * `cliVersion` value schema; "wonda" keeps the original `cli.version` key so
 * existing rows and readers stay valid, other products (per their
 * products/<product>.yaml manifest) get `cli.version:<product>`.
 */
export function getCliVersionSettingKey(product: string): string {
  return product === "wonda"
    ? AppSetting.cliVersion
    : `${AppSetting.cliVersion}:${product}`;
}

/**
 * Type-safe value types inferred from schemas.
 * Usage: `AppSettingValue["cliVersion"]` yields `{ latest: string; minSupported: string; updateCommand: string }`
 */
export type AppSettingValue = {
  [K in AppSettingName]: z.infer<(typeof appSettingSchemas)[K]>;
};

/**
 * Validates an app setting value against its schema.
 * Throws a ZodError if validation fails.
 *
 * @param settingName - The name of the setting (e.g., "cliVersion")
 * @param value - The value to validate
 * @returns The validated and typed value
 * @throws ZodError if validation fails
 */
export function validateAppSettingValue<K extends AppSettingName>(
  settingName: K,
  value: unknown,
): AppSettingValue[K] {
  return appSettingSchemas[settingName].parse(value) as AppSettingValue[K];
}

/**
 * Safely parses an app setting value against its schema.
 * Returns a result object instead of throwing.
 *
 * @param settingName - The name of the setting (e.g., "cliVersion")
 * @param value - The value to validate
 * @returns A SafeParseReturnType with success/error information
 */
export function safeParseAppSettingValue<K extends AppSettingName>(
  settingName: K,
  value: unknown,
): z.SafeParseReturnType<unknown, AppSettingValue[K]> {
  return appSettingSchemas[settingName].safeParse(
    value,
  ) as z.SafeParseReturnType<unknown, AppSettingValue[K]>;
}
